import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchUserStoryById } from "./userStoryService";
import TasksTable from "./TasksTable";
import "../assets/css/ViewCustomer.css";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";

function ViewUserStories({ onBack }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!userStoryId) return;
      setLoading(true);
      setError("");
      try {
        const data = await fetchUserStoryById(userStoryId);
        setStory(data || null);
        if (!data) setError("User story not found.");
      } catch (err) {
        console.error("Error fetching user story:", err);
        setError(err.response?.data?.message || "Failed to load user story.");
        setStory(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userStoryId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/view`);
    } else {
      navigate(`/projects/${projectId}/view`);
    }
  };

  const handleEdit = () => {
    if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/edit`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/edit`);
    }
  };

  const InfoItem = ({ label, value }) => {
    const hasValue = value != null && value !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading user story...</div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="viewlead-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div
          className="error-message"
          style={{ backgroundColor: "#fee", color: "#c33", padding: "12px", borderRadius: "4px", marginTop: "16px" }}
        >
          {error || "User story not found."}
        </div>
      </div>
    );
  }

  const s = story;
  const priority = s.Priority ?? s.priority ?? s.Priority_Level ?? s.priority_level ?? "";
  const status = s.Status ?? s.status ?? "";

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" className="btn-subproject-primary" onClick={handleEdit}>
          Edit User Story
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "nowrap",
          marginBottom: 20
        }}
      >
        <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
          <h3 className="viewlead-section-title">User Story Information</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="User Story ID" value={String(s.User_Story_Id ?? s.user_story_id ?? "")} />
            <InfoItem label="Title" value={s.Title || s.title} />
            <InfoItem label="Status" value={status} />
            <InfoItem label="Priority" value={priority || "Not Set"} />
            <InfoItem label="Project" value={s.Project_Name || s.project_name} />
            <InfoItem label="Subproject" value={s.Sub_Project_Name || s.sub_project_name} />
          </div>

          <h3 className="viewlead-section-title">Description</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Description" value={s.Description || s.description} />
          </div>
        </div>

        <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}>
          <h3 className="viewlead-section-title">Audit</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Created By" value={s.Created_By_Name || s.created_by_name} />
            <InfoItem label="Created On" value={formatDate(s.Created_On || s.created_on)} />
            <InfoItem label="Updated By" value={s.Updated_By_Name || s.updated_by_name} />
            <InfoItem label="Updated On" value={formatDate(s.Updated_On || s.updated_on)} />
          </div>
        </div>
      </div>

      <div className="viewlead-card tasks-table-card">
        <TasksTable
          cardTitle="Tasks"
          storyId={s.User_Story_Id ?? s.user_story_id ?? s.Story_Id ?? s.story_id}
          projectId={projectId}
          subProjectId={subprojectId ?? s.Sub_Project_Id ?? s.sub_project_id ?? undefined}
          storyTitle={s.Title || s.title}
          projectName={s.Project_Name || s.project_name}
          subProjectName={s.Sub_Project_Name || s.sub_project_name}
        />
      </div>
    </div>
  );
}

export default ViewUserStories;

